// import React from 'react';
import pick from 'lodash/pick';
import { notifyError, notifyWarn } from '@shakkerai/pro-components';
import { translate } from '@shakkerai/util-i18n';
import { isOverseas } from '@shakkerai/pro-utils';
import { getProjectState, getTempState, setSharedData, setTempState } from '@starflow/app/store';
import { requestModelVersionDetail, requetModelVersionsByIdList } from '@shakkerai/services';
import { BASETYPE_NAME_TO_VALUE, BASETYPE_VALUE_TO_NAME } from '@shakkerai/pro-models';
import { fallbackModelBaseParams, presetCheckpoints, presetCheckpointsMap } from '@shakkerai/pro-models';
import { getUnitData, restoreUnitData, updateUnitData } from '@starflow/app/helpers/flow';
import { MODEL_FLUX } from '@shakkerai/pro-models';
import { switchGlobalMode } from '../../generator/helpers/generate';
import { reportModelUsage } from '../store/actions';

export const baseTypeToNumber = (baseType) => {
  if (typeof baseType === 'string') {
    return BASETYPE_NAME_TO_VALUE[baseType];
  }
  return baseType;
};

export const fixLoraWeight = (weight) => {
  if (typeof weight !== 'number') return 0.8;
  if (weight > 2) return 0.8;

  return weight;
};

export const pickCheckpoint = (checkpoint, buttonId, callback) => {
  setTempState({
    tempPickingModel: true,
    tempCheckpoint: checkpoint,
    tempPickingButtonId: buttonId,
    tempCheckpointPickCallback: callback,
    exclusiveWindow: 'model-checkpoints',
  });
};

export const pickLora = (lora, buttonId, callback) => {
  setTempState({
    tempPickingModel: true,
    tempLoras: lora ? [lora] : [],
    tempPickingButtonId: buttonId,
    tempLoraPickCallback: callback,
    exclusiveWindow: 'model-loras',
  });
};

export const pickModelFields = (model, useVersionIntro) => {
  let extraData = {};

  if (useVersionIntro && model.versionIntro) {
    try {
      extraData = JSON.parse(model.versionIntro);
      if (Array.isArray(extraData.triggerWord)) {
        extraData.triggerWord = extraData.triggerWord.join(',');
      }
    } catch {
      // ...
    }
  }

  return pick(Object.assign({}, model, extraData), [
    'name',
    'versionId',
    'modelId',
    'versionName',
    'isHiddenStrategy',
    'uuid',
    'tagV2Ids',
    'baseType',
    'modelType',
    'runCount',
    'userAvatar',
    'userName',
    'imageUrl',
    'vipUsed',
    'weight',
    'modelUuid',
    'triggerWord',
    'from',
  ]);
};

// 提取模型结构化信息
export const pickVersionIntro = (versionData) => {
  if (!versionData?.versionIntro) return null;
  if (typeof versionData?.versionIntro !== 'string') return null;

  try {
    return JSON.parse(versionData?.versionIntro);
  } catch {
    return null;
  }
};

export const applyPresetModel = async (presetBaseModelId, unitId, restoreParams) => {
  if (!presetBaseModelId) return;

  if (restoreParams) {
    restoreUnitData(unitId);
  }

  const { loraModels, hiresOptions, controlnets } = getUnitData(unitId);
  const updateParams = { presetBaseModelId, checkpoint: null };

  const checkpointData = presetCheckpointsMap[presetBaseModelId].versionDetail;

  if (!checkpointData || !checkpointData.uuid) return;

  const currentLoraModels = (loraModels || []).filter((item) => !item.isPreset);

  updateParams.baseType = BASETYPE_NAME_TO_VALUE[checkpointData.baseType];

  if (checkpointData.loras) {
    // 如果预设模型有loraModels，则使用预设模型的loraModels
    // 切换preset的时候，要把当前loraModels中也是预设lora的项目去掉
    updateParams.loraModels = checkpointData.loras
      .concat(currentLoraModels)
      .filter((item, index, array) => array.findIndex((i) => i.versionId === item.versionId) === index)
      .slice(0, 5);
  } else {
    updateParams.loraModels = currentLoraModels;
  }

  const versionData = await requestModelVersionDetail(checkpointData.uuid).then((res) => res.data);
  const versionIntro = Object.assign({}, fallbackModelBaseParams[updateParams.baseType], pickVersionIntro(versionData));

  if (versionIntro.cfgScale) updateParams.cfgScale = versionIntro.cfgScale;
  if (versionIntro.cfg) updateParams.cfgScale = versionIntro.cfg;
  if (versionIntro.clipSkip) updateParams.clipSkip = versionIntro.clipSkip;
  if (versionIntro.samplerMethod) updateParams.samplerMethod = versionIntro.samplerMethod;
  if (versionIntro.samplerMethods?.[0] >= 0) updateParams.samplerMethod = String(versionIntro.samplerMethods[0]);

  if (versionIntro.samplingSteps) {
    updateParams.samplingSteps = versionIntro.samplingSteps;
  } else if (checkpointData.samplingSteps) {
    updateParams.samplingSteps = checkpointData.samplingSteps;
  } else {
    updateParams.samplingSteps = 20;
  }

  if (versionIntro.hdSamplerMethods?.[0] >= 0) {
    updateParams.hiresOptions = { ...hiresOptions, upscaler: String(versionIntro.hdSamplerMethods[0]) };
  }

  if (versionIntro.vae === 'none') {
    updateParams.vae = '-1';
  } else if (versionIntro.vae) {
    if (!isNaN(Number(versionIntro.vae))) {
      updateParams.vae = String(versionIntro.vae);
    }
  }

  if (updateParams.cfgScale) {
    updateParams.modelCfgScale = updateParams.cfgScale;
  }

  const hasFacialCN = controlnets.some((item) => item.type === 'face' && item.sourceImage?.url);

  if (hasFacialCN) {
    delete updateParams.cfgScale;
  }

  updateUnitData(unitId, updateParams);
};

const applyFixedCheckpoint = async (checkpointVersionId, unitId, baseType) => {
  const result = await requetModelVersionsByIdList([checkpointVersionId]);

  if (Array.isArray(result.data) && result.data.length) {
    const [item] = result.data;
    const { baseType: currentBaseType, checkpoint, presetBaseModelId } = getUnitData(unitId);

    const suggestionCheckpoint = {
      modelType: 'checkpoint',
      baseType: BASETYPE_VALUE_TO_NAME[item.baseType],
      imageUrl: item.cover,
      name: item.modelName,
      modelId: item.modelId,
      uuid: item.uuid,
      versionId: item.id,
      versionName: item.modelVersionName,
    };

    if (item.uuid === checkpoint?.uuid) {
      return;
    }

    if (item.uuid === presetCheckpointsMap[presetBaseModelId]?.versionDetail?.uuid) {
      return;
    }

    if (currentBaseType !== baseType) {
      applyModel(suggestionCheckpoint, unitId);
    } else {
      setTempState({ suggestionCheckpoint });
    }
  } else {
    throw new Error('No checkpoint found');
  }
};

export const getModelVersionDetail = async (versionUuid) => {
  const versionData = await requestModelVersionDetail(versionUuid).then((res) => res.data);

  if (!versionData) {
    throw new Error(translate('model_fetch_error'));
  }

  const nextBaseType = BASETYPE_NAME_TO_VALUE[versionData.baseType] || MODEL_FLUX;
  const versionIntro = Object.assign({}, fallbackModelBaseParams[nextBaseType], pickVersionIntro(versionData));

  return [versionData, versionIntro, nextBaseType];
};

export const applyModel = async (model, unitId, notToggle = false) => {
  if (!model?.versionId || !model?.uuid) return;

  const tempPickingModel = getTempState('tempPickingModel');

  if (tempPickingModel) {
    const modelData = pickModelFields(model, true);

    if (model.modelType?.toLowerCase() === 'lora') {
      if (getTempState('tempLoras', [])?.some((item) => item.versionId === modelData.versionId)) {
        setTempState('tempLoras', []);
        getTempState('tempLoraPickCallback')?.(null);
      } else {
        setTempState('tempLoras', [modelData]);
        getTempState('tempLoraPickCallback')?.(modelData);
      }
    } else {
      setTempState('tempCheckpoint', modelData);
      getTempState('tempCheckpointPickCallback')?.(modelData);
    }

    return;
  }

  const nextState = {};

  let versionData, versionIntro, nextBaseType;

  try {
    [versionData, versionIntro, nextBaseType] = await getModelVersionDetail(model.uuid);
  } catch (error) {
    notifyError(error?.message);
    console.log(`[DEBUG] error`, error);
  }

  model = Object.assign({}, pickModelFields(Object.assign({}, model, versionData)), { remark: null });
  delete model.relatedVersions;

  let isAddModel = true;
  let { checkpoint, loraModels, hiresOptions, controlnets, baseType: currentBaseType } = getUnitData(unitId);
  loraModels = loraModels || [];

  if (versionIntro?.triggerWord?.length) {
    model.triggerWord = versionIntro.triggerWord.join(',');
  }

  if (versionIntro?.ngPrompt) {
    model.ngPrompt = versionIntro.ngPrompt;
  }

  const modelType = model.modelType?.toLowerCase();

  if (modelType === 'checkpoint') {
    nextState.checkpoint = notToggle ? model : checkpoint && checkpoint?.versionId === model?.versionId ? null : model;
    nextState.baseType = nextState.checkpoint ? nextBaseType : MODEL_FLUX;

    if (nextState.checkpoint) {
      isAddModel = true;
      nextState.presetBaseModelId = null;
    } else {
      // 如果取消应用checkpoint，则尝试应用默认预设模型
      return applyPresetModel(presetCheckpoints[0].id, unitId);
    }
  } else if (modelType === 'lora') {
    if (!model.weight) {
      if (typeof versionIntro?.weight === 'number') {
        model.weight = versionIntro?.weight;
      } else {
        model.weight = 0.8;
      }

      if (model.weight > 2) {
        model.weight = 0.8;
      }
    }

    const loraToReplace = getTempState('loraToReplace');
    const isApplied = loraModels?.find((i) => i.versionId === model?.versionId);

    if (loraToReplace) {
      // 如果存在要替换的模型
      if (isApplied) {
        // 如果当前模型已经使用，则先清除掉
        nextState.loraModels = loraModels.filter((i) => i.versionId !== model?.versionId);
      }

      // 再替换
      nextState.loraModels = loraModels.map((item) => {
        if (item.versionId !== loraToReplace?.versionId) return item;
        return model;
      });

      setTempState({ loraToReplace: model });
    } else if (isApplied) {
      if (notToggle) {
        nextState.loraModels = loraModels;
      } else {
        // 如果已经使用，则取消使用
        nextState.loraModels = loraModels.filter((i) => i.versionId !== model?.versionId);
        isAddModel = false;
      }
    } else {
      nextState.loraModels = loraModels.concat(model);
    }
  }

  if (isAddModel) {
    reportModelUsage(model);

    if (versionIntro) {
      if (modelType === 'lora') {
        if (currentBaseType === MODEL_FLUX && nextBaseType === MODEL_FLUX) {
          // 如果是flux的lora，且当前底模预设也是flux，则不用切换底模
          // ...
        } else {
          let checkpointVersionId = null;
          let fallbackCheckpointId = null;

          if (nextBaseType === MODEL_FLUX) {
            // 对于flux的lora，推荐的底模就是通用预设底模
            nextState.presetBaseModelId = presetCheckpoints[0].id;
            nextState.checkpoint = null;
            nextState.baseType = MODEL_FLUX;
            // fallbackCheckpointId = checkpointVersionId =
            //   fallbackModelBaseParams[MODEL_FLUX]?.[isOverseas ? 'shakkerVersionId' : 'versionId'];
          } else if (versionIntro.ckpt?.length) {
            // 使用结构化数据里面的底模
            checkpointVersionId = Number(versionIntro.ckpt[0]);
          } else if (currentBaseType !== nextBaseType) {
            // 尝试使用兜底底模
            fallbackCheckpointId = checkpointVersionId =
              fallbackModelBaseParams[nextBaseType]?.[isOverseas ? 'shakkerVersionId' : 'versionId'];
          }

          if (checkpointVersionId) {
            if (!checkpoint || checkpoint.versionId !== checkpointVersionId) {
              applyFixedCheckpoint(checkpointVersionId, unitId, nextBaseType).catch((error) => {
                console.log(`[DEBUG] error`, error);

                if (fallbackCheckpointId) {
                  applyFixedCheckpoint(fallbackCheckpointId, unitId, nextBaseType).catch(console.error);
                }
              });
            }
          }
        }
      }

      // TODO
      // 如果是lora模型，则判断当前底模是否有结构化参数，不覆盖底模的结构化参数

      if (modelType === 'checkpoint') {
        if (versionIntro.cfgScale) nextState.cfgScale = versionIntro.cfgScale;
        if (versionIntro.cfg) nextState.cfgScale = versionIntro.cfg;
        if (versionIntro.samplingSteps) nextState.samplingSteps = versionIntro.samplingSteps;
      }

      if (versionIntro.clipSkip) nextState.clipSkip = versionIntro.clipSkip;
      if (versionIntro.samplerMethod) nextState.samplerMethod = versionIntro.samplerMethod;
      if (versionIntro.samplerMethods?.[0] >= 0) nextState.samplerMethod = String(versionIntro.samplerMethods[0]);

      if (versionIntro.hdSamplerMethods?.[0] >= 0) {
        nextState.hiresOptions = { ...hiresOptions, upscaler: String(versionIntro.hdSamplerMethods[0]) };
      }

      if (versionIntro.vae === 'none') {
        nextState.vae = '-1';
      } else if (versionIntro.vae) {
        if (!isNaN(Number(versionIntro.vae))) {
          nextState.vae = String(versionIntro.vae);
        }
      }
    }
  }

  if (nextState.cfgScale) {
    nextState.modelCfgScale = nextState.cfgScale;
  }

  const hasFacialCN = controlnets.some((item) => item.type === 'face' && item.sourceImage?.url);

  if (hasFacialCN) {
    delete nextState.cfgScale;
  }

  if (nextState.loraModels?.length > 5) {
    return notifyWarn(translate('model_lora_limitetoast'));
  }

  console.log(`[DEBUG] nextState`, nextState);
  updateUnitData(unitId, nextState);

  if (model.baseType !== BASETYPE_VALUE_TO_NAME[MODEL_FLUX]) {
    switchGlobalMode('pro');
  }
};

/**
 * 一次应用多个Lora，覆盖形式
 * @param {*} models
 * @param {*} unitId
 */
export const applyLoras = (models, unitId) => {
  const nextState = {};
  nextState.loraModels = models?.map((item) => {
    item.imageUrl = item.imageUrl || item.images?.[0]?.imageUrl;
    item.weight = !item.weight || isNaN(item.weight) ? 0.8 : item.weight;

    if (item.weight > 2) {
      item.weight = 0.8;
    }

    return pickModelFields(item);
  });

  updateUnitData(unitId, nextState);
};

export const switchSimpleModeCheckpoint = () => {};
